import React from 'react';
import { useExternalIntegrations } from '../../hooks/useExternalIntegrations';

/**
 * Renders its children only for a tenant permitted to use external
 * integrations. Task 1 wraps the Slack settings page (and its nav entry) in
 * this; every integration added later does the same and inherits the rule.
 *
 * Three states, deliberately distinct:
 *
 *   loading  — nothing. Not a spinner: a spinner on a nav item that is about
 *              to disappear is worse than no nav item, and this resolves in
 *              one request.
 *   denied   — `fallback` if given, otherwise a plain not-found panel. It says
 *              the page does not exist rather than "you lack permission",
 *              because a client-tenant admin should not learn that a
 *              Stemmler-only surface exists at all.
 *   allowed  — the children.
 *
 * This hides UI. It does not protect data — the server enforces the same flag
 * on every integration endpoint.
 */

interface Props {
  children: React.ReactNode;
  /** What to show when the tenant is not permitted. Pass `null` for a nav item. */
  fallback?: React.ReactNode;
}

const NotFoundPanel: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
    <h1 className="text-2xl font-semibold text-gray-900">Page not found</h1>
    <p className="mt-2 text-sm text-gray-500">
      The page you're looking for doesn't exist or has moved.
    </p>
  </div>
);

export const ExternalIntegrationsGate: React.FC<Props> = ({ children, fallback }) => {
  const { allowed, loading } = useExternalIntegrations();

  if (loading) return null;
  if (!allowed) return <>{fallback ?? <NotFoundPanel />}</>;
  return <>{children}</>;
};

export default ExternalIntegrationsGate;
